/**
 * voiceCompaction — vibe-designer 1.420.0
 *
 * Compacts the voice tab's transcript log the same way Pi chat compaction
 * (1.419.0) compacts agent messages: role-labeled transcript → ONE
 * non-streaming summarize call through the chat-proxy
 * (piCompaction.summarizeConversation, which carries the Bearer token +
 * `enable_thinking:false`) → a single `[compacted] <summary>` BRIDGE turn.
 *
 * `modelId` comes from the voice tab's model picker (voiceStore
 * settings.model); the default sentinel 'auto' is resolved by the tenant
 * LLM Router (same contract MIND Chat relies on — the chat-proxy is a
 * passthrough to the router).
 *
 * FAIL-SAFE: this module never mutates the store. It THROWS on empty input
 * or summarize failure; the caller replaces the log only on success. Note
 * for `piForwardScope: 'whole-transcript'` users: after compaction the
 * forward sends the summary turn instead of the raw log — intended, the
 * summary IS the transcript now.
 */
import type { VoiceTurn } from "@/stores/voiceStore";
import { summarizeConversation } from "./piCompaction";

/** Role-labeled single-line-per-turn transcript. Voice logs are short
 *  (HISTORY_MAX_TURNS-capped in voiceStore) — no char caps needed. */
export function buildVoiceTranscript(history: VoiceTurn[]): string {
  const lines: string[] = [];
  for (const turn of history) {
    const text = turn.text.trim();
    if (!text) continue;
    const label =
      turn.role === "user"
        ? "USER"
        : turn.role === "tool"
          ? `TOOL(${turn.tool?.name ?? "?"})`
          : "BRIDGE";
    lines.push(`${label}: ${text}`);
  }
  return lines.join("\n");
}

export interface VoiceCompactionOptions {
  history: VoiceTurn[];
  modelId: string;
  /** Same-origin chat-proxy base. Injectable for tests. */
  apiBase?: string;
}

/** Summarize the log into one bridge turn. Throws on empty/failed — the
 *  caller keeps the original history on any throw. */
export async function compactVoiceHistory(
  opts: VoiceCompactionOptions,
): Promise<VoiceTurn> {
  const transcript = buildVoiceTranscript(opts.history);
  if (!transcript) {
    throw new Error("nothing to compact — the voice transcript is empty");
  }
  const summary = await summarizeConversation({
    transcript,
    modelId: opts.modelId,
    apiBase: opts.apiBase,
  });
  return {
    role: "bridge",
    text: `[compacted] ${summary}`,
    timestamp: Date.now(),
  };
}
